import { useEffect, useState } from "react";

import { format } from "date-fns";
import {
  AlertCircle,
  CreditCard,
  Edit,
  FileText,
  Loader2,
  Trash2,
} from "lucide-react";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "~/components/ui/alert-dialog";
import { Button } from "~/components/ui/button";
import { Card, CardContent } from "~/components/ui/card";
import { EmptyState } from "~/components/ui/empty-state";
import { Skeleton } from "~/components/ui/skeleton";
import { useEnsureProfile } from "~/hooks/useEnsureProfile";
import { usePaymentProofs } from "~/hooks/usePaymentProofs";
import TabSectionHeader from "~/pages/clients/components/client-detail/TabSectionHeader";
import { PaymentProofDialog } from "~/pages/clients/components/client-detail/payment-proof/PaymentProofDialog";
import { SalesRequestLink } from "~/pages/clients/components/client-detail/sales-requests/SalesRequestLink";
import { useProfileStore } from "~/store/profileStore";
import { PaymentProof } from "~/types/payment";
import { formatCurrency } from "~/utils/helpers";

interface PaymentProofsTabProps {
  clientId: string;
}

export const PaymentProofsTab = ({ clientId }: PaymentProofsTabProps) => {
  const { role, id: userId } = useProfileStore();
  const {
    isProfileLoaded,
    isLoading: isProfileLoading,
    refreshProfile,
  } = useEnsureProfile();
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedProof, setSelectedProof] = useState<PaymentProof | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  // Force profile to load when component mounts
  useEffect(() => {
    if (!userId) {
      console.log("No user ID in PaymentProofsTab, forcing sync check");
      useProfileStore
        .getState()
        .forceSyncProfileCheck()
        .then((id) => {
          if (!id) {
            // If force sync fails, try the regular refresh
            refreshProfile();
          } else {
            console.log("User ID loaded via force sync:", id);
          }
        });
    }
  }, [userId, refreshProfile]);

  const {
    paymentProofs,
    loading,
    error,
    fetchPaymentProofs,
    removePaymentProof,
  } = usePaymentProofs(clientId);

  const handleAddClick = async () => {
    // Try to ensure profile is loaded first
    if (!userId) {
      // Try force sync first for immediate results
      const forcedId = await useProfileStore.getState().forceSyncProfileCheck();

      if (!forcedId) {
        // If force sync fails, try regular refresh
        await refreshProfile();
      }
    }

    // Check if we have a user ID now
    const currentId = useProfileStore.getState().id;

    if (currentId) {
      setSelectedProof(null);
      setIsAddDialogOpen(true);
    } else {
      console.error("Failed to load user profile. Cannot open dialog.");
      // Could show a toast message here
    }
  };

  const handleEditClick = async (proof: PaymentProof) => {
    // Try to ensure profile is loaded first
    if (!userId) {
      // Try force sync first
      const forcedId = await useProfileStore.getState().forceSyncProfileCheck();

      if (!forcedId) {
        // If force sync fails, try regular refresh
        await refreshProfile();
      }
    }

    // Check if we have a user ID now
    const currentId = useProfileStore.getState().id;

    if (currentId) {
      setSelectedProof(proof);
      setIsAddDialogOpen(true);
    } else {
      console.error("Failed to load user profile. Cannot open dialog.");
      // Could show a toast message here
    }
  };

  const handleDeleteClick = (proofId: string) => {
    setDeletingId(proofId);
    setIsDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (deletingId) {
      await removePaymentProof(deletingId);
      setIsDeleteDialogOpen(false);
      setDeletingId(null);
    }
  };

  const isSeller = role === "seller";
  const isAdmin = role === "admin" || role === "superadmin";
  const canAddProof = isSeller || isAdmin;

  if (loading) {
    return (
      <div className="space-y-6 p-4 pt-6 md:p-6">
        <div className="flex items-center justify-between">
          <Skeleton className="h-7 w-56" />
          <Skeleton className="h-10 w-44" />
        </div>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-32 w-full rounded-lg" />
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="m-4 mt-6 rounded-md border border-yellow-200 bg-yellow-50 p-4 md:m-6">
        <div className="flex">
          <div className="flex-shrink-0">
            <AlertCircle className="h-5 w-5 text-yellow-400" />
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-yellow-800">
              Error al cargar los comprobantes
            </h3>
            <div className="mt-2 text-sm text-yellow-700">
              <p>
                No se pudieron cargar los comprobantes de pago. Intente
                nuevamente.
              </p>
            </div>
            <div className="mt-4">
              <Button
                size="sm"
                variant="outline"
                onClick={() => fetchPaymentProofs()}
              >
                Reintentar
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-4 pt-6 md:p-6">
      <TabSectionHeader
        title="Comprobantes de Pago"
        description={
          paymentProofs.length > 0
            ? `${paymentProofs.length} comprobante${paymentProofs.length !== 1 ? "s" : ""}`
            : undefined
        }
        action={
          canAddProof ? (
            <Button onClick={handleAddClick} disabled={isProfileLoading}>
              {isProfileLoading ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <CreditCard className="mr-2 h-4 w-4" />
              )}
              Nuevo comprobante
            </Button>
          ) : undefined
        }
      />

      {paymentProofs.length === 0 ? (
        <EmptyState
          icon={FileText}
          title="No hay comprobantes de pago registrados"
          description="Los comprobantes de pago permiten registrar evidencia de pagos realizados por el cliente."
        />
      ) : (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
          {paymentProofs.map((proof) => (
            <Card key={proof.id} className="overflow-hidden">
              <CardContent className="p-0">
                <div className="p-4">
                  <div className="mb-2 flex items-start justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">
                        RUT {proof.rut}
                      </p>
                      <p className="mt-1 text-xl font-semibold tabular-nums">
                        {formatCurrency(proof.amount)}
                      </p>
                    </div>
                    <div className="flex">
                      {canAddProof && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-muted-foreground hover:text-foreground"
                          onClick={() => handleEditClick(proof)}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                      )}
                      {isAdmin && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-muted-foreground hover:bg-red-50 hover:text-red-600"
                          onClick={() => handleDeleteClick(proof.id)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </div>

                  <div className="space-y-1 text-sm text-muted-foreground">
                    <p>
                      Registrado el{" "}
                      {format(new Date(proof.createdAt), "dd/MM/yyyy")}
                    </p>
                    {proof.salesRequestId && (
                      <div>
                        <span>Nota de venta: </span>
                        <SalesRequestLink id={proof.salesRequestId} />
                      </div>
                    )}
                    {proof.comments && (
                      <p className="mt-2 whitespace-pre-line border-t pt-2 text-foreground">
                        {proof.comments}
                      </p>
                    )}
                  </div>
                </div>

                {proof.screenshotUrl && (
                  <div className="border-t">
                    <a
                      href={proof.screenshotUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block h-32 w-full overflow-hidden"
                    >
                      <img
                        src={proof.screenshotUrl}
                        alt="Comprobante"
                        className="h-full w-full object-cover transition-opacity hover:opacity-90"
                      />
                    </a>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <PaymentProofDialog
        clientId={clientId}
        isOpen={isAddDialogOpen && !!useProfileStore.getState().id}
        onOpenChange={(open) => {
          // Only allow opening if user ID is available
          const currentId = useProfileStore.getState().id;
          if (!open || currentId) {
            setIsAddDialogOpen(open);
          } else {
            console.error("Cannot open dialog when user ID is not available");
          }
        }}
        paymentProof={selectedProof || undefined}
        onSuccess={fetchPaymentProofs}
      />

      <AlertDialog
        open={isDeleteDialogOpen}
        onOpenChange={setIsDeleteDialogOpen}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar comprobante?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción no se puede deshacer. El comprobante será eliminado
              permanentemente.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              className="bg-red-600 hover:bg-red-700"
            >
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
